import { useEffect, useMemo, useState, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  Star,
  MessageSquare,
  X,
  Check,
  ArrowRight,
  ArrowLeft,
  Quote,
  Sparkles,
  Edit3,
  Search,
  ChevronDown,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase/firebase";

// ---- Configuration ----
const RATING_FIELDS = [
  { key: "overallExperience", label: "Overall experience" },
  { key: "culturalPrograms", label: "Cultural programs" },
  { key: "organization", label: "Organization & coordination" },
  { key: "cleanliness", label: "Cleanliness & hygiene" },
  { key: "parking", label: "Parking / accessibility" },
  { key: "hospitality", label: "Hospitality" },
  { key: "volunteers", label: "Helpfulness of volunteers" },
  { key: "crowdManagement", label: "Crowd & queue management" },
  { key: "foodPrasada", label: "Food / prasada arrangements" },
  { key: "volunteerImprovement", label: "Scope for volunteer improvement" },
  { key: "meaningfulProgram", label: "Most meaningful program" },
];

const COMMUNICATION_CHANNELS = [
  "WhatsApp",
  "Posters",
  "Social media",
  "Word of mouth",
  "Announcements at venue",
  "Other",
];

const DEFAULT_FORM = {
  name: "",
  overallExperience: 5,
  culturalPrograms: 5,
  organization: 5,
  cleanliness: 5,
  parking: 5,
  hospitality: 5,
  volunteers: 5,
  crowdManagement: 5,
  foodPrasada: 5,
  volunteerImprovement: 5,
  meaningfulProgram: 5,
  communicationChannel: "",
  otherFeedback: "",
};

const STEPS = ["Personal", "Experience", "Additional"];

export default function ReviewSection() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State (Share Feedback)
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  // Right Side Viewer & Zoom State
  const [searchTerm, setSearchTerm] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullFeedbackOpen, setIsFullFeedbackOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setReviews(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    document.body.style.overflow = (showModal || isFullFeedbackOpen) ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showModal, isFullFeedbackOpen]);

  // --- Filtering ---
  const filteredReviews = useMemo(() => {
    return reviews.filter(r => 
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      r.otherFeedback?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [reviews, searchTerm]);

  useEffect(() => {
    setCurrentIndex((prev) => {
      if (filteredReviews.length === 0) return 0;
      return Math.min(prev, filteredReviews.length - 1);
    });

    if (isFullFeedbackOpen && filteredReviews.length === 0) {
      setIsFullFeedbackOpen(false);
    }
  }, [filteredReviews.length, isFullFeedbackOpen]);

  // --- Manual Navigation ---
  const nextReview = () => {
    if (filteredReviews.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % filteredReviews.length);
  };

  const prevReview = () => {
    if (filteredReviews.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + filteredReviews.length) % filteredReviews.length);
  };

  // --- Stats Calculations ---
  const { overallStats, fieldStats } = useMemo(() => {
    let totalScore = 0;
    let validCount = 0;
    const fields = {};
    
    RATING_FIELDS.forEach(f => {
      fields[f.key] = { sum: 0, count: 0, avg: 0 };
    });

    reviews.forEach((r) => {
      const oRating = Number(r.overallExperience);
      if (oRating >= 1 && oRating <= 5) {
        totalScore += oRating;
        validCount++;
      }
      RATING_FIELDS.forEach(f => {
        const fRating = Number(r[f.key]);
        if (fRating >= 1 && fRating <= 5) {
          fields[f.key].sum += fRating;
          fields[f.key].count++;
        }
      });
    });

    RATING_FIELDS.forEach(f => {
      if (fields[f.key].count > 0) {
        fields[f.key].avg = (fields[f.key].sum / fields[f.key].count).toFixed(1);
      }
    });

    return {
      overallStats: {
        average: validCount ? (totalScore / validCount).toFixed(1) : 0,
        total: validCount,
      },
      fieldStats: fields
    };
  }, [reviews]);

  const renderStars = (count, size = 14) =>
    [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={size}
        strokeWidth={i < count ? 0 : 1.5}
        className={
          i < count
            ? "fill-[#D4AF37] text-[#D4AF37]"
            : "fill-[#E8DCC4]/40 text-[#E8DCC4]"
        }
      />
    ));

  // --- Modal Navigation ---
  const openModal = () => { setForm(DEFAULT_FORM); setStep(0); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setTimeout(() => { setStep(0); setForm(DEFAULT_FORM); }, 300); };
  const updateRating = (field, rating) => setForm((prev) => ({ ...prev, [field]: rating }));
  const goNext = () => {
    if (step === 0 && !form.name.trim()) return alert("Please enter your name");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submitReview = async () => {
    if (!form.name.trim()) return alert("Please enter your name");
    if (!form.communicationChannel) return alert("Please select a communication channel.");
    try {
      setSubmitting(true);
      await addDoc(collection(db, "reviews"), {
        ...form,
        name: form.name.trim(),
        otherFeedback: form.otherFeedback.trim(),
        createdAt: serverTimestamp(),
      });
      closeModal();
    } catch (error) {
      alert("Unable to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const safeCurrentIndex = filteredReviews.length > 0 ? Math.min(currentIndex, filteredReviews.length - 1) : 0;
  const currentReview = filteredReviews[safeCurrentIndex];

  return (
    <section className="relative w-full bg-[#FCF8F2] py-16 lg:py-24 overflow-hidden z-10 font-sans">
      <style>{`
        .luxury-scrollbar::-webkit-scrollbar { width: 4px; }
        .luxury-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .luxury-scrollbar::-webkit-scrollbar-thumb { background: #E8DCC4; border-radius: 10px; }
        .luxury-scrollbar::-webkit-scrollbar-thumb:hover { background: #D4AF37; }
      `}</style>

      {/* Ambient Background */}
      <div className="absolute top-10 right-0 -mr-40 h-[500px] w-[500px] rounded-full bg-[#D4AF37]/10 blur-[130px] pointer-events-none -z-10" />
      <div className="absolute bottom-0 left-0 -ml-40 h-[500px] w-[500px] rounded-full bg-[#722013]/5 blur-[120px] pointer-events-none -z-10" />

      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 h-auto lg:h-[650px]">
          
          {/* ================= LEFT SIDE: ANALYTICS & BREAKDOWN ================= */}
          <div className="w-full lg:w-[35%] flex flex-col h-[500px] lg:h-full bg-white/60 backdrop-blur-xl border border-[#E8DCC4]/60 rounded-[2rem] p-6 sm:p-8 shadow-sm">
            <div className="shrink-0 mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-serif text-[11px] font-bold uppercase tracking-[0.2em] text-[#722013]">
                  Community Reflections
                </span>
              </div>
              <h2 className="font-serif font-bold text-[#2a0b06] tracking-tight text-3xl sm:text-4xl mb-6">
                Voices of Chaturmasya
              </h2>
              
              <div className="flex items-center justify-between mb-8 border-b border-[#E8DCC4]/40 pb-6">
                <div className="flex items-center gap-4">
                  <span className="font-serif text-6xl font-bold text-[#722013] leading-none tracking-tighter">
                    {overallStats.average || "0"}
                  </span>
                  <div>
                    <div className="flex gap-0.5 mb-1.5">
                      {renderStars(Math.round(Number(overallStats.average || 0)), 16)}
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Based on {overallStats.total} {overallStats.total === 1 ? "Review" : "Reviews"}
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={openModal}
                className="w-full group flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-[#2a0b06] px-6 py-4 transition-all hover:bg-[#1a0704] hover:shadow-lg hover:shadow-[#2a0b06]/20 active:scale-[0.98]"
              >
                <Edit3 className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#FCF8F2]">
                  Share Your Feedback
                </span>
              </button>
            </div>

            {/* Scrollable Breakdown */}
            <div className="flex-1 overflow-y-auto luxury-scrollbar pr-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 sticky top-0 bg-[#FCF8F2]/90 backdrop-blur pb-2 z-10">
                Detailed Breakdown
              </h3>
              <div className="space-y-4">
                {RATING_FIELDS.map((field) => {
                  const avg = Number(fieldStats[field.key].avg || 0);
                  const widthPct = (avg / 5) * 100;
                  return (
                    <div key={field.key} className="flex flex-col gap-1.5">
                      <div className="flex justify-between items-center text-xs font-semibold text-[#2a0b06]">
                        <span>{field.label}</span>
                        <span className="font-serif text-[#722013]">{avg > 0 ? avg.toFixed(1) : "—"}</span>
                      </div>
                      <div className="h-1.5 w-full bg-[#E8DCC4]/30 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${widthPct}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#C1A67B]" 
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ================= RIGHT SIDE: SEARCH & COMPACT VIEWER ================= */}
          <div className="w-full lg:w-[65%] flex flex-col h-[500px] lg:h-full">
            
            {/* Search Bar */}
            <div className="shrink-0 relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#722013]/50" />
              <input
                type="text"
                placeholder="Search reviews by name or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-2xl border border-[#E8DCC4] bg-white/80 px-4 py-3.5 pl-11 text-sm font-serif text-[#2a0b06] shadow-sm outline-none transition-all placeholder:font-sans placeholder:text-slate-400 focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/10"
              />
            </div>

            {/* Manual Review Viewer */}
            <div className="flex-1 relative rounded-[2rem] border border-[#E8DCC4]/60 bg-white/40 shadow-inner backdrop-blur-md overflow-hidden flex flex-col items-center justify-center p-4 sm:p-8">
              
              {/* Zoom Controls */}
              <div className="absolute top-4 right-4 flex items-center gap-1 z-20 bg-white/80 backdrop-blur-md border border-[#E8DCC4] rounded-full p-1 shadow-sm">
                <button 
                  onClick={() => setZoomLevel(z => Math.max(0.7, z - 0.1))}
                  className="p-1.5 text-[#722013]/60 hover:text-[#722013] hover:bg-[#FCF8F2] rounded-full transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut size={16} />
                </button>
                <div className="w-px h-4 bg-[#E8DCC4]" />
                <button 
                  onClick={() => setZoomLevel(z => Math.min(1.3, z + 0.1))}
                  className="p-1.5 text-[#722013]/60 hover:text-[#722013] hover:bg-[#FCF8F2] rounded-full transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn size={16} />
                </button>
              </div>

              {/* Viewer Content */}
              {loading ? (
                <div className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">
                  Loading...
                </div>
              ) : filteredReviews.length === 0 ? (
                <div className="flex flex-col items-center text-center">
                  <MessageSquare size={32} className="text-[#D4AF37] mb-3" />
                  <p className="font-serif text-xl font-bold text-[#2a0b06]">No reviews found</p>
                </div>
              ) : currentReview ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  
                  {/* Manual Navigation Controls (Left/Right) */}
                  <button 
                    onClick={prevReview} 
                    className="absolute left-0 z-20 p-3 rounded-full bg-white/80 border border-[#E8DCC4] text-[#722013] hover:bg-[#FCF8F2] shadow-sm transition-transform hover:scale-105"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <button 
                    onClick={nextReview} 
                    className="absolute right-0 z-20 p-3 rounded-full bg-white/80 border border-[#E8DCC4] text-[#722013] hover:bg-[#FCF8F2] shadow-sm transition-transform hover:scale-105"
                  >
                    <ChevronRight size={20} />
                  </button>

                  <motion.div 
                    animate={{ scale: zoomLevel }} 
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="w-full max-w-md origin-center z-10 px-12"
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentReview.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="relative w-full bg-white border border-[#E8DCC4]/80 rounded-[1.75rem] p-6 shadow-xl shadow-[#722013]/5"
                      >
                        <Quote className="absolute top-4 right-4 h-8 w-8 text-[#FCF8F2] -rotate-12 z-0" />
                        
                        <div className="relative z-10 flex flex-col items-center text-center">
                          <div className="flex gap-1 mb-4">
                            {renderStars(Number(currentReview.overallExperience || 0), 16)}
                          </div>
                          
                          {/* Compact Text with Line Clamp */}
                          <p className="font-serif text-[#2a0b06] text-lg leading-[1.6] line-clamp-3 mb-5">
                            {currentReview.otherFeedback ? `"${currentReview.otherFeedback}"` : "An profoundly peaceful and uplifting experience."}
                          </p>
                          
                          {/* Chevron Trigger for Full Feedback */}
                          <button 
                            onClick={() => setIsFullFeedbackOpen(true)}
                            className="flex flex-col items-center justify-center gap-1 group mb-5"
                          >
                            <span className="text-[9px] font-bold uppercase tracking-widest text-[#D4AF37] transition-colors group-hover:text-[#722013]">
                              Read Full
                            </span>
                            <div className="grid h-6 w-6 place-items-center rounded-full bg-[#FCF8F2] border border-[#E8DCC4] text-[#722013] transition-all group-hover:translate-y-1">
                              <ChevronDown size={14} />
                            </div>
                          </button>

                          <div className="flex items-center gap-3 pt-4 border-t border-[#E8DCC4]/50 w-full justify-center">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FCF8F2] border border-[#D4AF37]/30 font-serif text-lg font-bold text-[#722013]">
                              {(currentReview.name || "?").trim().charAt(0).toUpperCase()}
                            </div>
                            <div className="text-left">
                              <h3 className="font-bold text-[#2a0b06] text-sm leading-tight">
                                {currentReview.name}
                              </h3>
                              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">
                                {currentReview.createdAt?.toDate?.().toLocaleDateString("en-IN", {
                                  month: "short", day: "numeric", year: "numeric",
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>
                </div>
              ) : null}
            </div>
            
            {/* Pagination Position Indicator */}
            {filteredReviews.length > 0 && (
              <div className="shrink-0 text-center mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Review {safeCurrentIndex + 1} of {filteredReviews.length}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= FULL FEEDBACK MODAL (CENTERED POPUP) ================= */}
      <AnimatePresence>
        {isFullFeedbackOpen && currentReview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#2a0b06]/60 backdrop-blur-sm p-4 sm:p-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative flex h-full max-h-[700px] w-full max-w-3xl flex-col overflow-hidden rounded-[2rem] border border-[#E8DCC4]/80 bg-white/95 p-1 shadow-2xl backdrop-blur-xl"
            >
              <div className="flex flex-1 flex-col overflow-hidden rounded-[1.75rem] border border-[#E8DCC4]/40 bg-gradient-to-b from-[#FCF8F2]/60 to-white">
                
                {/* Header inside Popup */}
                <div className="shrink-0 px-6 py-5 sm:px-8 sm:py-6 border-b border-[#E8DCC4]/60 flex items-center justify-between relative">
                  <div className="flex items-center gap-4 pr-10">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-[#D4AF37]/40 font-serif text-xl font-bold text-[#722013] shadow-sm">
                      {(currentReview.name || "?").trim().charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2a0b06] text-lg leading-tight">
                        {currentReview.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <div className="flex gap-0.5">
                          {renderStars(Number(currentReview.overallExperience || 0), 12)}
                        </div>
                        <span className="text-[#E8DCC4] hidden sm:inline">•</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {currentReview.createdAt?.toDate?.().toLocaleDateString("en-IN", {
                            month: "long", day: "numeric", year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsFullFeedbackOpen(false)}
                    className="absolute right-5 top-5 sm:right-6 sm:top-6 grid h-8 w-8 place-items-center rounded-full bg-[#FCF8F2] text-[#722013] transition-colors hover:bg-[#E8DCC4]"
                  >
                    <X size={16} strokeWidth={2.5} />
                  </button>
                </div>

                {/* Scrollable Full Text */}
                <div className="min-h-0 flex-1 overflow-y-auto luxury-scrollbar px-6 py-6 sm:px-8 sm:py-8">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentReview.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="font-serif text-[#2a0b06] text-xl sm:text-2xl leading-[1.8] whitespace-pre-wrap"
                    >
                      {currentReview.otherFeedback || "No additional feedback was provided for this reflection."}
                    </motion.p>
                  </AnimatePresence>
                </div>

                {/* Strict Footer: Only Back and Next Review */}
                <div className="shrink-0 px-6 py-4 sm:px-8 sm:py-5 border-t border-[#E8DCC4]/60 bg-[#FCF8F2]/50 flex items-center justify-between">
                  <button
                    onClick={() => setIsFullFeedbackOpen(false)}
                    className="group flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#E8DCC4] text-[10px] font-bold uppercase tracking-widest text-[#722013] transition-colors hover:bg-white hover:text-[#2a0b06]"
                  >
                    <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" /> Back
                  </button>

                  {filteredReviews.length > 1 && (
                    <button
                      onClick={nextReview}
                      className="group flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#2a0b06] text-[10px] font-bold uppercase tracking-widest text-[#FCF8F2] transition-colors hover:bg-[#1a0704]"
                    >
                      Next Review <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- Share Feedback Modal (Submission Form) --- */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#2a0b06]/40 backdrop-blur-sm p-4 sm:p-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative flex h-full max-h-[700px] w-full max-w-xl flex-col overflow-hidden rounded-[2rem] border border-[#E8DCC4]/80 bg-white/95 p-1 shadow-2xl backdrop-blur-xl"
            >
              <div className="flex flex-1 flex-col overflow-hidden rounded-[1.75rem] border border-[#E8DCC4]/40 bg-gradient-to-b from-[#FCF8F2]/60 to-white">
                <div className="relative border-b border-[#E8DCC4]/60 px-6 py-5 sm:px-8">
                  <button onClick={closeModal} className="absolute right-5 top-5 sm:right-6 sm:top-6 grid h-8 w-8 place-items-center rounded-full bg-[#FCF8F2] text-[#722013] transition-colors hover:bg-[#E8DCC4]">
                    <X size={16} strokeWidth={2.5} />
                  </button>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="font-serif text-[10px] font-bold uppercase tracking-[0.2em] text-[#722013]">Devotee Voice</span>
                  </div>
                  <h2 className="font-serif text-2xl font-bold text-[#2a0b06]">Share Your Experience</h2>
                  <div className="mt-5 flex items-center gap-3">
                    {STEPS.map((label, i) => (
                      <div key={label} className="flex flex-1 items-center gap-2 sm:gap-3">
                        <div className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold tracking-wider ${i < step ? "bg-[#D4AF37] text-[#2a0b06]" : i === step ? "bg-[#2a0b06] text-[#FCF8F2]" : "bg-[#FCF8F2] border border-[#E8DCC4] text-slate-400"}`}>
                          {i < step ? <Check size={10} strokeWidth={3} /> : i + 1}
                        </div>
                        <span className={`hidden sm:block text-[9px] font-bold uppercase tracking-[0.15em] ${i === step ? "text-[#2a0b06]" : "text-slate-400"}`}>
                          {label}
                        </span>
                        {i < STEPS.length - 1 && <div className="h-px flex-1 bg-[#E8DCC4]/50" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="luxury-scrollbar flex-1 overflow-y-auto px-6 py-5 sm:px-8 sm:py-6">
                  {step === 0 && (
                    <div className="mx-auto max-w-md space-y-6 mt-4">
                      <div>
                        <label className="mb-3 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#722013]">Your Name</label>
                        <input autoFocus placeholder="Enter your full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && goNext()} className="w-full border-b-2 border-[#E8DCC4] bg-transparent pb-2 text-lg font-serif text-[#2a0b06] outline-none transition-colors placeholder:text-slate-300 focus:border-[#D4AF37]" />
                      </div>
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-1.5 divide-y divide-[#E8DCC4]/40">
                      {RATING_FIELDS.map((field) => (
                        <div key={field.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2.5">
                          <label className="text-xs font-semibold text-[#2a0b06]">{field.label}</label>
                          <div className="flex gap-1.5">
                            {[1, 2, 3, 4, 5].map((n) => {
                              const active = form[field.key] === n;
                              return (
                                <button key={n} type="button" onClick={() => updateRating(field.key, n)} className={`flex h-8 w-8 items-center justify-center rounded-full border text-[10px] font-bold transition-all ${active ? "border-[#D4AF37] bg-[#D4AF37] text-[#2a0b06] shadow-sm" : "border-[#E8DCC4] bg-white text-slate-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
                                  {n}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <div>
                        <label className="mb-3 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#722013]">How did you hear about us?</label>
                        <div className="flex flex-wrap gap-2">
                          {COMMUNICATION_CHANNELS.map((channel) => {
                            const active = form.communicationChannel === channel;
                            return (
                              <button key={channel} type="button" onClick={() => setForm({ ...form, communicationChannel: channel })} className={`rounded-xl border px-3.5 py-1.5 text-[10px] font-bold tracking-wider uppercase transition-all ${active ? "border-[#2a0b06] bg-[#2a0b06] text-[#FCF8F2]" : "border-[#E8DCC4] bg-white text-[#722013] hover:border-[#2a0b06]"}`}>
                                {channel}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label className="mb-3 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#722013]">Additional Thoughts</label>
                        <textarea rows="4" placeholder="Share any reflections or suggestions..." value={form.otherFeedback} onChange={(e) => setForm({ ...form, otherFeedback: e.target.value })} className="w-full resize-none rounded-xl border border-[#E8DCC4] bg-white p-4 text-sm font-serif text-[#2a0b06] outline-none transition-colors placeholder:text-slate-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-[#E8DCC4]/60 bg-[#FCF8F2]/50 px-6 py-4 sm:px-8 sm:py-5 flex items-center justify-between">
                  <button onClick={() => (step === 0 ? closeModal() : setStep(step - 1))} className="group flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#722013] transition-colors hover:text-[#2a0b06]">
                    {step === 0 ? "Cancel" : <><ArrowLeft size={12} className="transition-transform group-hover:-translate-x-1" /> Back</>}
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button onClick={goNext} className="group flex items-center gap-1.5 rounded-xl bg-[#D4AF37] px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#2a0b06] transition-all hover:brightness-110">
                      Continue <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <button onClick={submitReview} disabled={submitting} className="flex items-center gap-1.5 rounded-xl bg-[#2a0b06] px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#FCF8F2] transition-all hover:bg-[#1a0704] disabled:opacity-50">
                      {submitting ? "Submitting..." : "Submit"} {!submitting && <Check size={12} />}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}